import {
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
} from "@/lib/help-parties";
import type {
  HelpArticle,
  HelpArticleId,
  HelpCategory,
} from "@/lib/registrant-help/types";

export const OMT_AMS_HELP_ARTICLE_IDS = [
  "getting-started",
  "dashboard",
  "registrations",
  "registration-details",
  "needs-attention",
  "compliance-issues",
  "parties",
  "party-exempt",
  "user-management",
  "account",
] as const satisfies readonly HelpArticleId[];

export type OmtAmsHelpArticleId = (typeof OMT_AMS_HELP_ARTICLE_IDS)[number];

export const OMT_AMS_HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "Sign in, verify email, and find your way around this portal.",
  },
  {
    id: "registrations",
    title: "Registrations",
    summary: "Browse properties that list your organization as OMT or AMS.",
  },
  {
    id: "organization",
    title: "Organization",
    summary: "Team invites, admins, and the account page.",
  },
];

export const OMT_AMS_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "A tour of the OMT/AMS portal: sign-in, the view-only dashboard, registrations, and who can invite teammates.",
    category: "getting-started",
    keywords: [
      "overview",
      "portal",
      "start",
      "home",
      "hera",
      "omt",
      "ams",
      "view-only",
    ],
    related: [
      "dashboard",
      "registrations",
      "parties",
      "user-management",
      "account",
    ],
    sections: [
      {
        heading: "What this portal is for",
        blocks: [
          {
            type: "p",
            text: "The OMT/AMS portal is where asset managers, OMTs, and financial-services contacts review the registrations that list their organization. It is view-only: you can see properties, statuses, answers, comments, and payment history, but you cannot register, pay, renew, cancel, or de-register from here.",
          },
          {
            type: "p",
            text: "Registrations belong to the organization primary, not to a single login. Anyone invited into the organization sees the same list.",
          },
        ],
      },
      {
        heading: "Sign in",
        blocks: [
          {
            type: "p",
            text: "Open the OMT, AMS & Financial Services login. Existing users enter their email and password. New users create a login with the email they will use for Hera. Forgot password is on that sign-in page.",
          },
          {
            type: "p",
            text: "After you sign in, Verify Your Email asks you to open the link sent to the address on the page. Use Resend Verification Email if the message is missing. You cannot use the dashboard until the email is verified.",
          },
        ],
      },
      {
        heading: "If you were invited",
        blocks: [
          {
            type: "p",
            text: "If an admin used Add User with your email, the next time you sign in you see Account Invitation. Accept, then confirm Confirm Account Join. Reject declines the invite and leaves you on your own account. Joining does not change which party is the organization primary.",
          },
        ],
      },
      {
        heading: "The main screens",
        blocks: [
          {
            type: "ul",
            items: [
              "Dashboard — list or map of registrations, with tabs for All Active, Needs Attention, Renewable, and Inactive. Admins also see Users.",
              "Users — people in the organization. Only admins see this tab.",
              "Account — the email and name on your login.",
              "Help — this help center. Search or browse topics. Many screens also have a Help button that opens the matching article without leaving the page.",
            ],
          },
        ],
      },
      {
        heading: "If the list is empty",
        blocks: [
          {
            type: "p",
            text: "Anyone can sign in. If no registration lists your organization’s primary as OMT or AMS, the dashboard says No properties list you as OMT or AMS. You can still open Help, Account, and (if you are an admin) Users.",
          },
        ],
      },
      {
        heading: "Finding help",
        blocks: [
          {
            type: "p",
            text: "Use Help in the header to open this help center. On the dashboard and on registration details, the Help button opens that topic in a side panel. From the panel you can open the full article here, including related topics and search.",
          },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    summary:
      "Tabs, list versus map, the Needs Attention tile, and opening a registration.",
    category: "getting-started",
    keywords: ["dashboard", "tabs", "map", "list", "needs attention", "tiles"],
    related: [
      "registrations",
      "needs-attention",
      "compliance-issues",
      "registration-details",
      "user-management",
    ],
    sections: [
      {
        heading: "What you see",
        blocks: [
          {
            type: "p",
            text: "The dashboard is the home for registrations that list your organization as OMT or AMS. A Needs Attention tile appears when those properties need review for a late renewal and/or an open compliance issue. If properties are scheduled to be paid later, a Bulk Payment tile can list them so you can view that set — it does not start a payment.",
          },
        ],
      },
      {
        heading: "Tabs",
        blocks: [
          {
            type: "ul",
            items: [
              "All Active Registrations — current registrations that are not on the inactive list.",
              "Needs Attention — late renewals (30 to 90 days past renewal, or 30 or more days when that license type’s historical data has been cleaned, and the OMT is not marked exempt) and/or open compliance issues. See Needs Attention.",
              "Renewable — registrations that are in the renewal window or expired.",
              "Inactive Registrations — cancelled, de-registered, or otherwise inactive records.",
              "Users — organization people. Only admins see this tab.",
            ],
          },
        ],
      },
      {
        heading: "List and map",
        blocks: [
          {
            type: "p",
            text: "When a registration tab is selected, use the list/map control next to the tabs. List shows the table, including a Registrant column. Map shows pins for the same set. Click a row or pin to open details.",
          },
          {
            type: "p",
            text: "There is no Actions column. This portal does not offer Pay Now, Renew, Cancel, or De-register on the list.",
          },
        ],
      },
    ],
  },
  {
    id: "registrations",
    title: "Registrations",
    summary:
      "What appears on each tab, how statuses are shown, and why this list is view-only.",
    category: "registrations",
    keywords: [
      "license",
      "status",
      "compliant",
      "renewable",
      "needs attention",
      "inactive",
      "registrant",
    ],
    related: [
      "dashboard",
      "registration-details",
      "needs-attention",
      "compliance-issues",
    ],
    sections: [
      {
        heading: "What a registration is",
        blocks: [
          {
            type: "p",
            text: "A registration (sometimes labeled as a license) is one property under one license type in a jurisdiction. You see it here when your organization’s primary party is listed as OMT or AMS on that registration.",
          },
        ],
      },
      {
        heading: "Statuses you may see",
        blocks: [
          {
            type: "ul",
            items: [
              "Balance Due, Pay Later, Payment Failed, Payment Processing, or awaiting check or invoice — fees or a payment are outstanding. You can review the status; the registrant portal is where payments are made.",
              "Compliant until renewal — approved and current.",
              "Rejected — the jurisdiction did not approve it.",
              "Cancelled or de-registered — the registration is inactive and appears on the Inactive tab.",
            ],
          },
        ],
      },
      {
        heading: "How the tabs differ",
        blocks: [
          {
            type: "ul",
            items: [
              "All Active — registrations that are still active.",
              "Needs Attention — late renewals and/or open compliance issues (same properties as the Needs Attention tile).",
              "Renewable — records in the renewal window or expired.",
              "Inactive — ended or withdrawn records.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "needs-attention",
    title: "Needs Attention",
    summary:
      "Which OMT/AMS registrations need attention: late renewals and open compliance issues (view-only).",
    category: "registrations",
    keywords: [
      "needs attention",
      "late renewal",
      "compliance",
      "30",
      "90",
      "exempt",
    ],
    related: [
      "compliance-issues",
      "dashboard",
      "registrations",
      "party-exempt",
    ],
    sections: [
      {
        heading: "What is included",
        blocks: [
          {
            type: "ul",
            items: [
              "Late renewal — renewal date 30 to 90 days in the past, or 30 or more days when historical data for that license type has been cleaned. OMT not marked exempt, and status still eligible for the late-renewal rule.",
              "Active compliance issue — an open issue or Ready for Check until the raising party clears it.",
            ],
          },
          {
            type: "p",
            text: "This portal is view-only. The registrant receives compliance emails and reports issues resolved from the registrant portal. When your organization is the OMT and is marked Exempt, late-renewal Needs Attention stays off and registration and late fees due are zero. The registrant still has to register and renew. See Exempt.",
          },
        ],
      },
    ],
  },
  {
    id: "compliance-issues",
    title: "Compliance issues",
    summary:
      "How compliance issues appear on registrations you monitor. You cannot flag or clear issues in this portal.",
    category: "registrations",
    keywords: [
      "compliance",
      "issue",
      "ready for check",
      "comments",
      "view-only",
    ],
    related: ["needs-attention", "registration-details", "registrations"],
    sections: [
      {
        heading: "What you can see",
        blocks: [
          {
            type: "p",
            text: "When government or Hera Registry flags a compliance issue, it appears under Issues & Comments on the registration and can place the property on Needs Attention. The registrant’s primary is emailed when the issue is raised.",
          },
          {
            type: "p",
            text: "You can read the issue and comments here. You cannot Add Compliance Issue, Report Issue Resolved, or Clear Compliance Issue from the OMT/AMS portal.",
          },
        ],
      },
    ],
  },
  {
    id: "registration-details",
    title: "Registration details",
    summary:
      "The details sheet and page: property, questions, payment history, and comments — view-only.",
    category: "registrations",
    keywords: [
      "details",
      "view",
      "questions",
      "comments",
      "property",
      "history",
      "compliance",
    ],
    related: [
      "registrations",
      "dashboard",
      "compliance-issues",
      "needs-attention",
    ],
    sections: [
      {
        heading: "Opening details",
        blocks: [
          {
            type: "p",
            text: "Click a registration on the dashboard to open details in a side sheet. You can expand that sheet or open the full License Details page. Both show status, property, questions, payment history, and comments.",
          },
        ],
      },
      {
        heading: "What you can review",
        blocks: [
          {
            type: "ul",
            items: [
              "Property information and registration answers.",
              "Payment history for that registration.",
              "Comments and compliance issue banners from the organization or the jurisdiction (view-only).",
            ],
          },
          {
            type: "p",
            text: "There is no Actions card. You cannot pay, renew, reapply, cancel, or de-register from this portal.",
          },
        ],
      },
    ],
  },
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
  {
    id: "user-management",
    title: "User management",
    summary:
      "Primary versus secondary users, admins, invites, and disable. The primary party cannot be changed.",
    category: "organization",
    keywords: [
      "users",
      "admin",
      "invite",
      "invitation",
      "primary",
      "secondary",
      "disable",
      "add user",
    ],
    related: ["getting-started", "account", "dashboard"],
    sections: [
      {
        heading: "Overview",
        blocks: [
          {
            type: "p",
            text: "User management controls who belongs to this organization and who can invite or disable people. Every organization has one primary party and any number of invited secondary users. Registrations belong to the primary. The primary party cannot be transferred from this portal.",
          },
        ],
      },
      {
        heading: "What every user can do",
        blocks: [
          {
            type: "p",
            text: "Anyone in the organization who is not disabled can open the dashboard and review the same registrations, including details, comments, and payment history.",
          },
        ],
      },
      {
        heading: "Primary party",
        blocks: [
          {
            type: "p",
            text: "The primary is the organization owner listed on registrations as OMT or AMS. It is always an admin, and that admin status cannot be turned off. There is no Make Primary action.",
          },
        ],
      },
      {
        heading: "Admins",
        blocks: [
          {
            type: "p",
            text: "Only admins can open the Users tab. Non-admins cannot see or change the organization user list.",
          },
          {
            type: "p",
            text: "Admins can:",
          },
          {
            type: "ul",
            items: [
              "Invite people with Add User",
              "Cancel a pending invite",
              "Disable or re-enable a secondary user",
              "Grant or revoke Admin for a secondary user",
            ],
          },
        ],
      },
      {
        heading: "Inviting and managing users",
        blocks: [
          {
            type: "p",
            text: "On the Users tab, Add User asks for Email Address. When that email next logs in, they are asked to confirm they want to join your account.",
          },
          {
            type: "ul",
            items: [
              "If they do not have a login yet, they create one with that same email and verify it. Then Account Invitation appears.",
              "If they already have a login, Account Invitation appears the next time they sign in.",
              "They Accept (then confirm Confirm Account Join) or Reject. Joining does not change the primary.",
              "Pending rows show Account Type as Pending. Admin stays locked until they join.",
              "Cancel removes a pending invite.",
              "Disable stops their login. Re-enable restores access.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "account",
    title: "Account",
    summary:
      "The email and name on your login, and how to reset your password.",
    category: "organization",
    keywords: [
      "account",
      "profile",
      "password",
      "email",
      "name",
      "reset password",
    ],
    related: ["getting-started", "user-management"],
    sections: [
      {
        heading: "Your profile",
        blocks: [
          {
            type: "p",
            text: "The Account page shows the email and name on your OMT/AMS login. This portal is view-only; you do not edit company profile fields or billing settings here.",
          },
        ],
      },
      {
        heading: "Password",
        blocks: [
          {
            type: "p",
            text: "Use forgot-password on the OMT, AMS & Financial Services sign-in page to change your password. Sign in again with the new password.",
          },
        ],
      },
    ],
  },
];

const ARTICLES_BY_ID = new Map(
  OMT_AMS_HELP_ARTICLES.map((article) => [article.id, article]),
);

export function getOmtAmsHelpArticle(id: string): HelpArticle | undefined {
  return ARTICLES_BY_ID.get(id as HelpArticleId);
}

export function isOmtAmsHelpArticleId(id: string): id is OmtAmsHelpArticleId {
  return ARTICLES_BY_ID.has(id as HelpArticleId);
}

export function omtAmsArticlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return OMT_AMS_HELP_ARTICLES.filter(
    (article) => article.category === categoryId,
  );
}
